import { Component, computed, DestroyRef, inject, OnInit, signal, Signal } from '@angular/core';
import { idMapToArray, ScopeService } from '../../../../../@core/utils/scope.service';
import {
  NbAccordionModule,
  NbFormFieldModule,
  NbInputModule,
  NbListModule,
  NbOptionModule,
  NbSelectModule,
} from '@nebular/theme';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { FormGroupFrom, IInvestmentWizard, NomenclatureTechniqueStepState } from '../wizard-interface';
import { KPI } from '../../../../../@core/api';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-nomenclature-technique-selection',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbFormFieldModule,
    TranslateModule,
    NbOptionModule,
    NbSelectModule,
    ReactiveFormsModule,
    NbAccordionModule,
  ],
  template: `
    <div class="flex-grow-1">
      <form [formGroup]="form" class="investment-form" autocomplete="off">
        <div class="form-group">
          <!--            nomenclature -->
          <!-- SUPER CATEGORY -->
          <div class="form-field"
               [class.attention]="!form.get('super_category')?.value"
               [class.selected]="!!form.get('super_category')?.value">
            <label for="super_category">{{ 'Super category' | translate }}</label>
            <nb-select id="super_category" fullWidth formControlName="super_category"
                       (selectedChange)="nomenclatureChanged('super_category', $event)"
                       placeholder="-">
              @for (superCategory of filteredSuperCategories(); track superCategory.id) {
                <nb-option [value]="superCategory">
                  {{ superCategory.name }}
                </nb-option>
              }
            </nb-select>
          </div>

          @if (fullNomenclature()) {
            <ng-container *ngTemplateOutlet="nomenclatureDetailed"></ng-container>
          } @else {
            <nb-accordion>
              <nb-accordion-item expanded="false" class="accordion-item-as-button">
                <nb-accordion-item-header>
                  {{ "Optional" | translate }}
                </nb-accordion-item-header>
                <nb-accordion-item-body>
                  <ng-container *ngTemplateOutlet="nomenclatureDetailed"></ng-container>
                </nb-accordion-item-body>
              </nb-accordion-item>
            </nb-accordion>
          }

          <ng-template #nomenclatureDetailed>
            <!-- CATEGORY -->
            <div class="form-field" [class.disabled]="!filteredCategories().length"
                 [class.attention]="fullNomenclature() && !form.get('category')?.value"
                 [class.selected]="fullNomenclature() && !!form.get('category')?.value">
              <label for="category">{{ 'Category' | translate }}</label>
              <nb-select id="category" fullWidth placeholder="-"
                         (selectedChange)="nomenclatureChanged('category', $event)"
                         formControlName="category">
                @for (category of filteredCategories(); track category.id) {
                  <nb-option [value]="category">
                    {{ category.name }}
                  </nb-option>
                }
              </nb-select>
            </div>

            <!-- SUB CATEGORY -->
            <div class="form-field" [class.disabled]="!filteredSubCategories()?.length"
                 [class.attention]="fullNomenclature() && !form.get('sub_category')?.value"
                 [class.selected]="fullNomenclature() && !!form.get('sub_category')?.value">
              <label for="sub_category">{{ 'Sub category' | translate }}</label>
              <nb-select id="sub_category" fullWidth placeholder="-"
                         (selectedChange)="nomenclatureChanged('sub_category', $event)"
                         formControlName="sub_category">
                @for (subCategory of filteredSubCategories(); track subCategory.id) {
                  <nb-option [value]="subCategory">
                    {{ subCategory.name }}
                  </nb-option>
                }
              </nb-select>
            </div>
          </ng-template>
        </div>
      </form>
    </div>
  `,
  styleUrls: ['../wizard-index.component.scss', '../../../side-forms/base-form/base-form.component.scss',],
})
export class WizardNomenclatureTechniqueComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'NOMENCLATURE_TECHNIQUE_SELECTION';
  state = () => this.wizardService.getState(this.stateKey);
  // service
  private fb = inject(FormBuilder);
  private wizardService = inject(InvestmentWizardService);
  private scope = inject(ScopeService);
  private destroyRef = inject(DestroyRef);

  // main form
  form: FormGroupFrom<NomenclatureTechniqueStepState> = new FormGroup({
    super_category: this.fb.control<KPI.SuperCategory>(null, [Validators.required]),
    category: this.fb.control<KPI.Category>(null, [Validators.required]),
    sub_category: this.fb.control<KPI.SubCategory>(null, [Validators.required]),
  });

  // state
  private config = toSignal(this.scope.config$, { initialValue: null });
  filteredSuperCategories: Signal<KPI.SuperCategory[]> = toSignal(this.scope.superCategories$.pipe(idMapToArray()));
  private selectedSuperCategory = signal<KPI.SuperCategory>(null);

  private categories = toSignal(this.scope.categories$.pipe(idMapToArray()));
  private selectedCategory = signal<KPI.Category>(null);
  filteredCategories = computed(() => {
    return this.categories()?.filter((category) => category.parent === this.selectedSuperCategory()?.id);
  });

  private subCategories: Signal<KPI.SubCategory[]> = toSignal(this.scope.subCategories$.pipe(idMapToArray()));
  private selectedSubCategory = signal<KPI.SubCategory>(null);
  filteredSubCategories: Signal<KPI.SubCategory[]> = computed(() =>
    this.subCategories()?.filter((subCategory) => subCategory.parent === this.selectedCategory()?.id),
  );

  ngOnInit(): void {
    this.form.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((value) => {
        this.wizardService.setState(this.stateKey, <NomenclatureTechniqueStepState>value)
        this.validate();
      })
    ).subscribe();

    if (this.state()) {
      this.form.setValue(this.state());
      this.selectedSuperCategory.set(this.form.get('super_category').value);
      this.selectedCategory.set(this.form.get('category').value);
      this.selectedSubCategory.set(this.form.get('sub_category').value);
    } else {
      this.validate();
    }
  }

  private validate() {
    if (this._validateForm()) this.wizardService.unblockCurrentStep();
    else this.wizardService.blockCurrentStep('Please fill in all required fields');
  }

  private _validateForm() {
    const fullNomenclature = this.fullNomenclature();
    if (fullNomenclature) return this.form.valid
    else return this.form.get('super_category').valid;
  }

  fullNomenclature() {
    const config = this.config();
    return !!config?.investmentFullNomenclature;
  }

  nomenclatureChanged(level: 'super_category' | 'category' | 'sub_category', value: any) {
    if (level === 'super_category') {
      this.selectedSuperCategory.set(value);
      this.selectedCategory.set(null);
      this.selectedSubCategory.set(null);
      this.form.patchValue({ category: null, sub_category: null });
    } else if (level === 'category') {
      this.selectedCategory.set(value);
      this.selectedSubCategory.set(null);
      this.form.patchValue({ sub_category: null });
    } else if (level === 'sub_category') {
      this.selectedSubCategory.set(value);
    }
  }

}
